import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPct, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { TeamLogo } from "@/components/TeamLogo";
import { LeagueChip, LeagueLogo } from "@/components/LeagueLogo";
import { TrendingUp, Target, DollarSign, Flame, ChevronRight, Sparkles } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { ScheduleCard } from "@/components/ScheduleCard";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [picks, setPicks] = useState([]);
  const [rankings, setRankings] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, p, r] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/top-picks"),
          api.get("/dashboard/rankings", { params: { limit: 8 } }),
        ]);
        setStats(s.data); setPicks(p.data); setRankings(r.data);
      } catch (e) { /* handled by UI */ }
    })();
  }, []);

  return (
    <div className="mx-auto max-w-7xl space-y-8" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Your intelligence cockpit. Simulations, edges, and bets at a glance."
        testid="dashboard-header"
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="ROI" value={stats ? `${stats.roi.toFixed(1)}%` : "—"}
          icon={TrendingUp} accent="green" delta={stats?.roi ?? 0}
          testid="kpi-roi"
        />
        <StatCard label="Win rate" value={stats ? `${stats.win_rate}%` : "—"}
          icon={Target} accent="secondary" delta={stats ? stats.win_rate - 50 : 0}
          testid="kpi-winrate"
        />
        <StatCard label="Net profit" value={stats ? fmtMoney(stats.profit) : "—"}
          icon={DollarSign} accent="primary" testid="kpi-profit"
        />
        <StatCard label="Current streak" value={stats ? (stats.streak >= 0 ? `W${stats.streak}` : `L${Math.abs(stats.streak)}`) : "—"}
          icon={Flame} accent="orange" testid="kpi-streak"
        />
      </div>

      <ScheduleCard />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Top picks */}
        <div className="iso-card lg:col-span-2">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h3 className="font-lexend">Today's top edges</h3>
              <p className="text-xs text-gray-500">Ranked by expected value across moneylines, spreads, and totals.</p>
            </div>
            <Link to="/games" className="iso-btn-ghost !py-1.5 !text-xs" data-testid="view-all-games-link">
              View all <ChevronRight className="size-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {picks.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-500">Crunching simulations...</div>
            )}
            {picks.map((p, i) => (
              <Link
                key={p.game_id + p.market + p.selection}
                to={`/simulate?game=${p.game_id}`}
                className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`top-pick-${i}`}
              >
                <div className="flex size-10 items-center justify-center rounded-lg bg-white border border-border">
                  <LeagueLogo code={p.league} size={28} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900">{p.matchup}</div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {p.market} · {p.selection} {p.line ? `(${p.line > 0 ? "+" : ""}${p.line})` : ""} · {p.odds > 0 ? `+${p.odds}` : p.odds}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="iso-label !mb-0">EV</div>
                    <div className={`font-lexend text-lg font-bold ${p.ev > 0 ? "text-green-dark" : "text-red-dark"}`}>
                      {p.ev > 0 ? "+" : ""}{p.ev.toFixed(2)}%
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="iso-label !mb-0">Win %</div>
                    <div className="font-lexend text-lg font-bold text-gray-900">{fmtPct(p.prob, 0)}</div>
                  </div>
                  <span className={`iso-chip ${p.confidence === "HIGH" ? "iso-chip-green" : p.confidence === "MEDIUM" ? "iso-chip-blue" : ""}`}>
                    {p.confidence}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* ELO Rankings */}
        <div className="iso-card">
          <div className="border-b border-border px-6 py-4">
            <h3 className="font-lexend">ELO rankings</h3>
            <p className="text-xs text-gray-500">Power ratings across leagues.</p>
          </div>
          <div className="p-4">
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={rankings} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
                  <XAxis dataKey="abbr" axisLine={false} tickLine={false} fontSize={11} />
                  <YAxis domain={["dataMin - 30", "dataMax + 30"]} axisLine={false} tickLine={false} fontSize={11} />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} contentStyle={{ border: "1px solid rgb(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="elo" fill="rgb(var(--secondary-default))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1.5">
              {rankings.slice(0, 5).map((t, i) => (
                <div key={t.id} className="flex items-center gap-3 py-1.5" data-testid={`ranking-row-${i}`}>
                  <div className="w-5 text-xs font-semibold text-gray-400">{i + 1}</div>
                  <TeamLogo src={t.logo} abbr={t.abbr} color={t.color} size={28} />
                  <div className="flex-1 text-xs font-medium text-gray-900 truncate">{t.name}</div>
                  <div className="font-lexend text-xs font-bold text-gray-700">{t.elo.toFixed(0)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legacy banner */}
      <div className="iso-card overflow-hidden">
        <div className="flex flex-col items-start justify-between gap-4 p-6 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-xl bg-secondary text-secondary-foreground">
              <Sparkles className="size-6" />
            </div>
            <div>
              <div className="iso-label">Legacy score</div>
              <div className="font-lexend text-2xl font-bold text-gray-900">
                {stats?.legacy_score ?? 0} pts
              </div>
              <p className="text-xs text-gray-500">Grows with every simulation run and verified winning bet.</p>
            </div>
          </div>
          <Link to="/simulate" className="iso-btn-secondary" data-testid="run-sim-cta">
            <Sparkles className="size-4" /> Run a simulation
          </Link>
        </div>
      </div>
    </div>
  );
}
